import { Head, useForm } from '@inertiajs/react';
import { useRef, useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import FormSelect from '@/Components/FormSelect';
import FormTextarea from '@/Components/FormTextarea';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import { ArrowLeftIcon, PhotoIcon, DocumentIcon, CubeIcon, DocumentTextIcon, LinkIcon } from '@heroicons/react/24/outline';

function FileUploadField({ label, icon: Icon, accept, hint, inputRef, fileName, onFileChange, onRemove, error }) {
    return (
        <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
            <div
                className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                    fileName ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-600'
                } ${error ? 'border-red-300' : ''}`}
                onClick={() => inputRef.current?.click()}
            >
                {fileName ? (
                    <div className="flex items-center gap-2 justify-center">
                        <Icon className="w-5 h-5 text-emerald-700" />
                        <span className="text-sm text-slate-700 truncate">{fileName}</span>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onRemove(); }}
                            className="text-red-400 hover:text-red-600 text-xs ml-2"
                        >
                            Remove
                        </button>
                    </div>
                ) : (
                    <>
                        <Icon className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Click to upload</p>
                        {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
                    </>
                )}
            </div>
            <input ref={inputRef} type="file" accept={accept} onChange={onFileChange} className="hidden" />
            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
        </div>
    );
}

export default function Create({ flash }) {
    const imageRef = useRef(null);
    const certificateRef = useRef(null);
    const model3dRef = useRef(null);
    const cvRef = useRef(null);

    const [fileNames, setFileNames] = useState({ image: '', certificate: '', model3d: '', cv: '' });

    const { data, setData, post, processing, errors, progress } = useForm({
        type: 'url',
        title: '',
        description: '',
        url: '',
        image: null,
        certificate: null,
        model3d: null,
        cv: null,
    });

    const handleFile = (field, ref) => (e) => {
        const file = e.target.files[0];
        if (file) {
            setData(field, file);
            setFileNames(prev => ({ ...prev, [field]: file.name }));
        }
    };

    const removeFile = (field, ref) => () => {
        setData(field, null);
        setFileNames(prev => ({ ...prev, [field]: '' }));
        if (ref.current) ref.current.value = '';
    };

    const submit = (e) => {
        e.preventDefault();
        post('/qr-codes', {
            forceFormData: true,
        });
    };

    return (
        <AppLayout>
            <Head title="Create QR Code" />
            <PageHeader
                title="Create QR Code"
                subtitle="Generate a new QR code"
                actions={
                    <Button href="/qr-codes" variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </Button>
                }
            />

            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            <form onSubmit={submit} className="space-y-6">
                {/* Basic Information */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Basic Information</h3>
                    </div>
                    <CardBody>
                        <div className="space-y-6 max-w-xl">
                            <FormInput
                                label="Title"
                                value={data.title}
                                onChange={e => setData('title', e.target.value)}
                                error={errors.title}
                                required
                                placeholder="e.g. Product Catalog, Company Profile"
                            />
                            <FormSelect
                                label="Type"
                                value={data.type}
                                onChange={e => setData('type', e.target.value)}
                                error={errors.type}
                                required
                            >
                                <option value="url">URL - Redirect to a website</option>
                                <option value="file">File - Display uploaded content</option>
                            </FormSelect>
                            <FormTextarea
                                label="Description"
                                value={data.description}
                                onChange={e => setData('description', e.target.value)}
                                error={errors.description}
                                rows={3}
                                placeholder="Optional description for this QR code..."
                            />
                        </div>
                    </CardBody>
                </Card>

                {/* URL Input (for URL type) */}
                {data.type === 'url' && (
                    <Card>
                        <div className="px-6 py-4 border-b border-slate-100">
                            <h3 className="font-semibold text-slate-800">Redirect URL</h3>
                        </div>
                        <CardBody>
                            <div className="max-w-xl">
                                <div className="relative">
                                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <input
                                        type="url"
                                        value={data.url}
                                        onChange={e => setData('url', e.target.value)}
                                        placeholder="https://example.com"
                                        className={`w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20 ${errors.url ? 'border-red-300' : ''}`}
                                    />
                                </div>
                                {errors.url && <p className="mt-1 text-xs text-red-500">{errors.url}</p>}
                                <p className="mt-1 text-xs text-slate-400">
                                    When scanned, the QR code will redirect to this URL.
                                </p>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* File Uploads (for File type) */}
                {data.type === 'file' && (
                    <Card>
                        <div className="px-6 py-4 border-b border-slate-100">
                            <h3 className="font-semibold text-slate-800">Upload Files</h3>
                            <p className="text-xs text-slate-400 mt-1">All file uploads are optional. Upload the files you need.</p>
                        </div>
                        <CardBody>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FileUploadField
                                    label="Product Image"
                                    icon={PhotoIcon}
                                    accept=".jpg,.jpeg,.png"
                                    hint="JPG, PNG up to 20MB"
                                    inputRef={imageRef}
                                    fileName={fileNames.image}
                                    onFileChange={handleFile('image', imageRef)}
                                    onRemove={removeFile('image', imageRef)}
                                    error={errors.image}
                                />
                                <FileUploadField
                                    label="Certificate"
                                    icon={DocumentIcon}
                                    accept=".pdf,.jpg,.png"
                                    hint="PDF, JPG, PNG up to 100MB"
                                    inputRef={certificateRef}
                                    fileName={fileNames.certificate}
                                    onFileChange={handleFile('certificate', certificateRef)}
                                    onRemove={removeFile('certificate', certificateRef)}
                                    error={errors.certificate}
                                />
                                <FileUploadField
                                    label="3D Model"
                                    icon={CubeIcon}
                                    accept=".glb"
                                    hint="GLB file up to 500MB"
                                    inputRef={model3dRef}
                                    fileName={fileNames.model3d}
                                    onFileChange={handleFile('model3d', model3dRef)}
                                    onRemove={removeFile('model3d', model3dRef)}
                                    error={errors.model3d}
                                />
                                <FileUploadField
                                    label="CV / Document"
                                    icon={DocumentTextIcon}
                                    accept=".pdf,.doc,.docx"
                                    hint="PDF, DOC, DOCX up to 20MB"
                                    inputRef={cvRef}
                                    fileName={fileNames.cv}
                                    onFileChange={handleFile('cv', cvRef)}
                                    onRemove={removeFile('cv', cvRef)}
                                    error={errors.cv}
                                />
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Upload Progress */}
                {progress && (
                    <Card>
                        <CardBody>
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-600">Uploading...</span>
                                    <span className="font-medium text-slate-800">{progress.percentage}%</span>
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-2">
                                    <div className="bg-emerald-700 h-2 rounded-full transition-all" style={{ width: `${progress.percentage}%` }} />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Submit */}
                <div className="flex gap-3">
                    <Button type="submit" disabled={processing}>
                        {processing ? 'Creating...' : 'Create QR Code'}
                    </Button>
                    <Button href="/qr-codes" variant="outline">Cancel</Button>
                </div>
            </form>
        </AppLayout>
    );
}
